import React, { memo, useCallback, useMemo, useState, useEffect } from 'react'
import { Alert, Modal, Spin } from 'antd'
import { DefaultJsonForm } from '../../../../../../components/v2/default-json-form'
import schema from './schema.json'
import { convertData2JsonForm } from '../../../../../../shared/lib/convertData2JsonForm'
import { isFormDataRequired } from '../../../../../../../lib/utils/collections'
import fetchAPI from '../../../../../../../lib/utils/fetch-api'
import dayjs from 'dayjs'

interface DismissModalProps {
  isModalOpen: boolean
  employeeId: number
  onOk?: (() => void) | undefined
  onCancel?: ((e: React.MouseEvent<HTMLElement, MouseEvent>) => void) | undefined
  enums?: any
}

interface IFormData {
  dismissal_reason_id?: number
  dismissal_date?: string
  comment?: string
}

interface ISchemaActual {
  resultObject: Record<string, never>
  resultSchema: {
    properties: {
      dismissal_reason_id: any
      dismissal_date: any
      comment: any
    }
    required: string[]
  }
}

export const DismissModal: React.FC<DismissModalProps> = memo((props: DismissModalProps) => {
  const { isModalOpen, employeeId, onOk, onCancel, enums } = props
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [formData, setFormData] = useState<IFormData>({
    dismissal_reason_id: undefined,
    dismissal_date: undefined,
    comment: undefined,
  })

  const actualSchema = useMemo(() => {
    const schemaActual = convertData2JsonForm(schema.fields) as never as ISchemaActual

    if (schemaActual.resultSchema && schemaActual.resultSchema.properties) {
      // Добавляем enum для причины увольнения
      // Enum-ы приходят с бэкенда с ключом 'employee_dismissal_reason' (без префикса 'hr_')
      if (enums?.employee_dismissal_reason) {
        const oneOf = enums.employee_dismissal_reason.map((item: any) => ({
          const: item.id,
          title: item.value,
        }))
        const enumValues = enums.employee_dismissal_reason.map((item: any) => item.id)

        schemaActual.resultSchema.properties.dismissal_reason_id = {
          ...schemaActual.resultSchema.properties.dismissal_reason_id,
          type: 'string',
          oneOf,
          enum: enumValues,
          view: {
            name: 'select-from-enum',
          },
          options: {
            ...schemaActual.resultSchema.properties.dismissal_reason_id.options,
            allowClear: true,
            disabled: loading,
          },
        }
      }

      // Делаем все поля disabled при загрузке
      Object.keys(schemaActual.resultSchema.properties).forEach(key => {
        if (!schemaActual.resultSchema.properties[key].options) {
          schemaActual.resultSchema.properties[key].options = {}
        }
        schemaActual.resultSchema.properties[key].options.disabled = loading
      })
    }

    return schemaActual.resultSchema || {}
  }, [enums, loading])

  const handleOk = useCallback(async () => {
    const isRequiredFields = isFormDataRequired(formData, actualSchema)
    if (!isRequiredFields) {
      Modal.warning({
        title: 'Предупреждение',
        content: 'Заполните все обязательные поля',
      })
      return
    }

    try {
      setLoading(true)
      setError(null)
      await fetchAPI(`/api/hr/employees/${employeeId}/dismiss`, {
        method: 'POST',
        body: JSON.stringify(formData),
      })
      onOk?.()
    } catch (err: any) {
      setError(err?.message || 'Не удалось уволить сотрудника')
    } finally {
      setLoading(false)
    }
  }, [formData, actualSchema, employeeId, onOk])

  const handleCancel = (e: React.MouseEvent<HTMLElement>) => {
    setFormData({
      dismissal_reason_id: undefined,
      dismissal_date: undefined,
      comment: undefined,
    })
    setError(null)
    onCancel?.(e)
  }

  const onChange = useCallback(data => {
    setFormData(prevData => {
      return {
        ...prevData,
        ...data.formData,
      }
    })
  }, [])

  // Проверяем, изменились ли значения увольнения
  const isDismissalUnchanged = useMemo(
    () => formData.dismissal_reason_id === undefined,
    [formData.dismissal_reason_id],
  )

  // Устанавливаем значения по умолчанию при открытии модалки
  useEffect(() => {
    if (isModalOpen) {
      setFormData({
        dismissal_reason_id: undefined,
        dismissal_date: dayjs().toISOString(),
        comment: undefined,
      })
      setError(null)
    }
  }, [isModalOpen])

  if (!isModalOpen) {
    return null
  }

  return (
    <Modal
      title={schema.title}
      onOk={handleOk}
      onCancel={handleCancel}
      open={isModalOpen}
      destroyOnHidden={true}
      okButtonProps={{
        disabled: loading || isDismissalUnchanged,
      }}
      cancelButtonProps={{
        disabled: loading,
      }}
      closable={!loading}
      keyboard={!loading}
      maskClosable={!loading}
      okText="Подтвердить"
    >
      {error && <Alert message={error} type="error" showIcon style={{ marginBottom: 16 }} />}
      <Spin spinning={loading}>
        <DefaultJsonForm
          formData={formData}
          schema={actualSchema}
          onChange={onChange}
          orientation="horizontal"
        />
      </Spin>
    </Modal>
  )
})

DismissModal.displayName = 'DismissModal'
